import { Injectable } from '@angular/core';
import { AdminMessages } from '@app/admin/admin.messages';
import { TagType } from '@app/domain/tag/model/tag-type.model';
import { CreateTagDto } from '@app/models/tags/create-tag.model';
import { Tag } from '@app/domain/tag/model/tag.model';
import { Globals } from '@app/shared/globals/globals';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { Observable, firstValueFrom } from 'rxjs';
import { TagAPIService } from './tag-api.service';
import { SwalUtils } from '@app/shared/utils/swal.utils';
import { InFlightRegistry } from '@app/shared/utils/in-flight-registry';

@Injectable({
  providedIn: 'root'
})
export class TagBusinessService {

  // key -> the write currently in flight for that tag. A duplicate call while one is in flight is
  // coalesced onto the same promise (see InFlightRegistry) rather than rejected.
  private _updating: InFlightRegistry;

  constructor(
    public globals: Globals,
    private tagAPI: TagAPIService,
    private notifyUtils: NotifyUtils,
    private swalUtils: SwalUtils
  ) {
    this._updating = new InFlightRegistry();
  }

  get updating(): number[] {
    return this._updating.keys<number>().filter((key): key is number => typeof key === 'number');
  }

  get(
    ids: number[],
    name: string,
    type: TagType[]
  ): Observable<Tag[]> {
    return this.tagAPI.get(ids, name, type);
  }

  create(createDto: CreateTagDto): Promise<Tag> {
    return firstValueFrom(this.tagAPI.create(createDto))
      .then(res => {
        this.notifyUtils.notify(AdminMessages.TAGS.SUCCESS_TAG_CREATE);
        return res;
      });
  }

  update(id: number, updateDto: CreateTagDto): Promise<Tag> {
    return this._updating.run(id, () =>
      firstValueFrom(this.tagAPI.update(id, updateDto))
        .then(res => {
          this.notifyUtils.notify(AdminMessages.TAGS.SUCCESS_TAG_UPDATE);
          return res;
        })
    );
  }

  delete(id: number): Promise<Tag> {
    return this._updating.run(id, () =>
      firstValueFrom(this.tagAPI.delete(id))
        .then(res => {
          this.notifyUtils.notify(AdminMessages.TAGS.SUCCESS_DELETE_TAG_SINGLE);

          if (this.globals.user.position && this.globals.user.position.id === id) {
            this.globals.user.position = null;
          }

          return res;
        })
    );
  }

  deleteMultiple(ids: number[]): Promise<Tag[]> {
    return this._updating.run(`delete-multiple:${[...ids].sort((a, b) => a - b).join(',')}`, () =>
      firstValueFrom(this.tagAPI.deleteMultiple(ids))
        .then(res => {
          this.notifyUtils.notify(AdminMessages.TAGS.SUCCESS_DELETE_TAG_MULTIPLE);

          if (this.globals.user.position && ids.includes(this.globals.user.position.id)) {
            this.globals.user.position = null;
          }

          return res;
        })
    );
  }
}
